import type { ReactNode } from 'react';
import { BrandBolt } from '@/components/brand-bolt';
import { LocaleSwitch } from '@/components/locale-switch';
import { ThemeToggle } from '@/components/theme';
import './legal.css';

// Контакт для юр-страниц — один на все три документа.
export const LEGAL_EMAIL = 'support@scriptera.ai';

// Каркас юридической страницы (/privacy, /oferta, /data-deletion): логотип — ТОЛЬКО
// канонический BrandBolt (молния в кружке, смещена; см. logo-icon-rules), подвал —
// подпись как в наших письмах (notify-catalog.ts FOOTER: «Scriptera — бот, который отвечает
// клиентам вместо вас»; владелец 08.09.2026 заменил прежнее «автоматизации под ключ»).
// Без next-intl — тексты статичные и живут в самих страницах, локаль — параметр маршрута.
export function LegalShell({
  locale,
  title,
  updated,
  className,
  above,
  bare,
  sheetTop,
  headExtra,
  children,
}: {
  locale: string;
  /** Заголовок документа (h1). Нет — заголовок рисует содержимое само (статья справки, HelpArticle). */
  title?: string;
  /** Дата редакции документа. У справки её нет — строки тогда просто не будет. */
  updated?: string;
  /** Дополнительный класс каркаса (справка расширяет колонку: .legal.help). */
  className?: string;
  /** Кусок НАД заголовком — «крошки» статьи справки. */
  above?: ReactNode;
  /** ГОЛЫЙ РЕЖИМ (`?bare=1`, 29.09.2026): страница открыта листом внутри приложения —
   *  без шапки сайта (ссылка на главную с тарифами в приложении недопустима, CLAUDE.md
   *  п.7) и без подвала; свою шапку с крестиком рисует лист. */
  bare?: boolean;
  /** ШАПКА ЛИСТА НА ТЕЛЕФОНЕ (`?from=help|account`, сверка 03.10 Н1, кадр iOS K17-help-privacy):
   *  документ, открытый из справки или «Аккаунта», на телефоне в браузере стоит как слой
   *  кабинета — «×» (назад туда, откуда пришли) и название документа на строке шапки, без
   *  шапки сайта (логотип, язык, тема) и подвала. На широком экране страница как была. */
  sheetTop?: { href: string; closeLabel: string; title: string };
  /** КНОПКА ШАПКИ ЛЕВЕЕ ЯЗЫКА И ТЕМЫ (ТЗ HELP-WARM-FIX §6а п.2): «поделиться» статьи справки — такой же кнопкой
   *  `.tctl`; кнопки прижаты вправо, поэтому язык и тема при её появлении не сдвигаются. */
  headExtra?: ReactNode;
  children: ReactNode;
}) {
  const en = locale === 'en';
  const cls = ['legal', className, sheetTop ? 'has-stop' : ''].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      {sheetTop ? (
        <div className="legal-stop k-glass-head">
          <a className="legal-stop-x" href={sheetTop.href} aria-label={sheetTop.closeLabel}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </a>
          <span className="legal-stop-t">{sheetTop.title}</span>
        </div>
      ) : null}
      {bare ? null : (
      <header>
        <div className="wrap head-row">
          <a href={`/${locale}`} className="brand">
            <BrandBolt size={26} />
            Scriptera
          </a>
          {/* Язык и тема (владелец 2026-07-29): те же аккуратные кнопки, что справа
              сверху на главной (.tctl лендинга — стили скопированы в legal.css). */}
          <div className="topctl">
            {headExtra}
            <LocaleSwitch compact className="tctl" />
            <ThemeToggle compact className="tctl" />
          </div>
        </div>
      </header>
      )}
      <main>
        <div className="wrap">
          {above}
          {title ? <h1>{title}</h1> : null}
          {updated ? <p className="upd">{updated}</p> : null}
          {children}
        </div>
      </main>
      {bare ? null : (
      <footer>
        <div className="wrap sig">
          <BrandBolt size={22} />
          <span>
            {en
              ? 'Scriptera — more followers and more sales on Instagram'
              : 'Scriptera — поможем увеличить подписки и продажи в Инстаграме'}
          </span>
        </div>
      </footer>
      )}
    </div>
  );
}
